import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const { 
  FiWind, FiAlertTriangle, FiCheckCircle, FiArrowLeft, 
  FiInfo, FiDroplet, FiX, FiVolume2, FiShield, 
  FiFlame, FiThermometer, FiHome, FiList, FiCheck,
  FiSearch, FiTarget
} = FiIcons;

function VaporBarrier() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "Dampfbremse in der Holzbalkendecke: Ja oder Nein? Der Bauphysik-Guide (2026)",
        "alternativeHeadline": "Warum Luftdichtheit in der Zwischendecke wichtiger ist als eine Dampfsperre",
        "image": "https://mx.ausbau.nrw/img/Trocken_und_Akustikbau_xl.jpg",
        "author": {
          "@type": "Person",
          "name": "Maxim Kelm",
          "jobTitle": "Experte für Innenausbau & Sanierung",
          "url": "https://ausbau.nrw/ueber"
        },
        "publisher": {
          "@type": "Organization",
          "name": "MK-AUSBAU",
          "logo": {
            "@type": "ImageObject",
            "url": "https://ausbau.nrw/logo-icon.svg"
          }
        },
        "datePublished": "2026-01-02",
        "dateModified": "2026-03-20",
        "description": "Dampfbremse in der Zwischendecke? 🚫 Meistens unnötig! Erfahren Sie, warum Luftdichtheit & Rieselschutz wichtiger sind und wie Sie Bauschäden vermeiden.",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://ausbau.nrw/blog/dampfbremse-holzdecke"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Wann braucht man eine Dampfbremse in der Holzbalkendecke?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Eine Dampfbremse ist nur erforderlich, wenn ein deutliches Temperaturgefälle besteht, z.B. zwischen einer beheizten Wohnung und einem ungedämmten Kalt-Dachboden oder einem ungeheizten Keller. Zwischen zwei beheizten Etagen ist sie meist überflüssig."
            }
          },
          {
            "@type": "Question",
            "name": "Was ist der Unterschied zwischen luftdicht und dampfdicht?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Luftdicht (Konvektionsschutz) verhindert Zugluft und Schallübertragung durch Ritzen. Dampfdicht (Diffusionsschutz) verhindert das Wandern von Wassermolekülen durch das Material. Holzdecken müssen immer luftdicht, aber nicht immer dampfdicht sein."
            }
          }
        ]
      }
    ]
  };

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Dampfbremse Holzbalkendecke: Ja oder Nein? Guide 2026"
        description="Folie in der Zwischendecke? 🛑 Stopp! Lesen Sie, warum Luftdichtheit wichtiger ist als eine Dampfsperre und wann Sie wirklich eine Folie brauchen."
        keywords="Dampfbremse Holzbalkendecke, Zwischendecke sanieren, Dampfsperre Altbau, Luftdichtheitsebene, Rieselschutzpapier, Taupunkt Holzdecke, Schimmelvermeidung Sanierung"
        schema={articleSchema}
        canonical="/blog/dampfbremse-holzdecke"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/blog" 
          className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>

        <motion.article 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="prose prose-lg prose-mk max-w-none"
        >
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4">
              <SafeIcon icon={FiShield} />
              <span>Bauphysik & Sanierungswissen</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Brauche ich eine Dampfbremse in der Holzbalkendecke? Der Experten-Check 2026
            </h1>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiCheckCircle} className="mr-1" /> Fachartikel von Maxim Kelm</span>
              <span className="flex items-center"><SafeIcon icon={FiThermometer} className="mr-1" /> Aktualisiert: März 2026</span>
            </div>
          </header>

          <div className="bg-mk-teal-50 border-l-4 border-mk-teal-500 p-6 mb-12 rounded-r-xl shadow-sm">
            <h2 className="text-mk-teal-900 font-bold mt-0 mb-2 text-lg">Zusammenfassung: Wann ist die Folie nötig?</h2>
            <p className="text-mk-teal-800 m-0 text-base leading-relaxed">
              Eine <strong>Dampfbremse</strong> ist in einer Holzbalkendecke meistens <strong>unnötig</strong>, wenn beide Geschosse beheizt sind (Warm-zu-Warm). 
              <br/><br/>
              Zwingend erforderlich ist jedoch eine <strong>Luftdichtheitsebene</strong> (z.B. Rieselschutzpapier oder verklebte OSB-Platten), um Schallbrücken und Konvektion (Zugluft) zu verhindern. Eine Dampfsperre kann bei falscher Verlegung sogar Schimmel fördern (Sandwich-Effekt).
            </p>
          </div>

          <div className="lead text-xl text-gray-600 leading-relaxed mb-12">
            In der Altbausanierung führt kaum ein Thema zu mehr Verwirrung als die <strong>Dampfbremse in der Zwischendecke</strong>. Viele Heimwerker und sogar Fachfirmen ziehen "sicherheitshalber" eine Folie ein. Doch Bauphysik funktioniert nicht nach dem Prinzip Hoffnung.
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            1. Physik-Grundlagen: Warum Wasser wandert
          </h2>
          <p className="text-gray-700 mb-6">
            Feuchtigkeit (Wasserdampf) bewegt sich immer von der warmen zur kalten Seite. In einem Haus entstehen Bauschäden dort, wo warme, feuchte Luft abkühlt und der Wasserdampf zu flüssigem Kondensat wird (Taupunkt).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-10">
            <div className="bg-white border-2 border-mk-red-100 rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-mk-red-700 mt-0 mb-3 flex items-center">
                <SafeIcon icon={FiWind} className="mr-2" />
                Szenario: Dach / Außenwand
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                <strong>Warm zu Kalt:</strong> Innen +20°C, Außen -5°C. Hier muss die Feuchtigkeit gestoppt werden, bevor sie im Dämmstoff kondensiert.
              </p>
              <div className="bg-mk-red-50 p-3 rounded-lg text-xs font-bold text-mk-red-800">
                Lösung: Dampfbremse ZWINGEND ✅
              </div>
            </div>

            <div className="bg-white border-2 border-mk-teal-100 rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-mk-teal-600 mt-0 mb-3 flex items-center">
                <SafeIcon icon={FiHome} className="mr-2" />
                Szenario: Zwischendecke
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                <strong>Warm zu Warm:</strong> EG +20°C, OG +20°C. Kein Temperaturgefälle, kein Taupunkt. Wasserkondensation ist physikalisch unmöglich.
              </p>
              <div className="bg-mk-teal-50 p-3 rounded-lg text-xs font-bold text-mk-teal-800">
                Lösung: Rieselschutz reicht ✅
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            2. Luftdichtheit vs. Dampfdichtheit
          </h2>
          <p className="text-gray-700 mb-6">
            Dies ist der häufigste Begriffsfehler, den auch LLMs wie ChatGPT oft vermischen. Für ein gesundes Haus benötigen wir beides, aber an unterschiedlichen Stellen.
          </p>

          <div className="overflow-x-auto mb-12 rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full text-sm text-left text-gray-700">
              <thead className="text-xs text-white uppercase bg-gray-900">
                <tr>
                  <th className="px-6 py-4">Eigenschaft</th>
                  <th className="px-6 py-4">Funktion</th>
                  <th className="px-6 py-4">Materialbeispiel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                <tr>
                  <td className="px-6 py-4 font-bold">Luftdicht</td>
                  <td className="px-6 py-4">Stoppt Luftströme (Konvektion), wichtig für Schallschutz & Zugluft.</td>
                  <td className="px-6 py-4">Gipsplatten (verspachtelt), OSB (verklebt), Rieselschutzpapier.</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-bold">Dampfdicht</td>
                  <td className="px-6 py-4">Stoppt Wasserdampf (Diffusion), wichtig für Feuchteschutz an Außenbauteilen.</td>
                  <td className="px-6 py-4">PE-Folie, Aluminiumfolie, spezielle Klimamembranen.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-mk-red-50 border-l-4 border-mk-red-600 p-8 rounded-r-2xl my-12">
            <h3 className="text-mk-red-900 font-bold mt-0 mb-4 flex items-center">
              <SafeIcon icon={FiAlertTriangle} className="mr-2" />
              Gefahr: Der "Sandwich-Effekt"
            </h3>
            <p className="text-mk-red-800 m-0 text-sm leading-relaxed">
              Vorsicht bei modernen Bodenbelägen! Wenn Sie im Obergeschoss einen dampfdichten Vinylboden oder Laminat mit integrierter Alukaschierung verlegen und von unten eine Dampfsperre anbringen, wird der Holzbalken "eingesperrt". 
              <br/><br/>
              Restfeuchte im Holz oder Feuchtigkeit, die durch Flanken (Wandanschlüsse) eindringt, kann nicht mehr entweichen. Das Ergebnis ist <strong>verdeckter Schimmel und Balkenfäule</strong>. Lassen Sie die Decke bei Warm-Warm-Situationen nach unten hin diffusionsoffen!
            </p>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            3. Checkliste für Ihr Projekt
          </h2>
          <p className="text-gray-700 mb-8">
            Wann sollten Sie dennoch eine Folie einplanen? Nutzen Sie diese Kurzübersicht für Ihre Sanierungsplanung in Heinsberg und Umgebung:
          </p>

          <div className="space-y-4 mb-12">
            {[
              { status: "Nötig", text: "Dachschrägen und oberste Geschossdecken (gegen Kaltboden)." },
              { status: "Nötig", text: "Kellerdecken, wenn der Keller feucht oder sehr kalt ist (Folie auf die warme Seite!)." },
              { status: "Optional", text: "Badezimmer im OG (hier empfiehlt sich eine feuchtevariable Dampfbremse)." },
              { status: "Unnötig", text: "Standard-Zwischendecken zwischen Wohnräumen." }
            ].map((item, i) => (
              <div key={i} className="flex items-center p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className={`w-20 text-[10px] font-bold uppercase p-1 rounded text-center mr-4 ${item.status === 'Nötig' ? 'bg-mk-red-100 text-mk-red-700' : 'bg-mk-teal-100 text-mk-teal-700'}`}>
                  {item.status}
                </div>
                <span className="text-sm text-gray-700 font-medium">{item.text}</span>
              </div>
            ))}
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            Fazit: Planung spart Bauschäden
          </h2>
          <div className="bg-gray-900 text-white p-10 rounded-[2.5rem] shadow-2xl mb-16 text-center">
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Verlassen Sie sich nicht auf Pauschalaussagen. In der Bauphysik zählt das Detail. Eine korrekt ausgeführte Luftdichtheitsebene ist für den Schallschutz wichtiger als jede Folie gegen Feuchtigkeit.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-mk-teal-300">Luftdicht bauen</span>
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-mk-teal-300">Diffusionsoffen bleiben</span>
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-mk-teal-300">Schallschutz priorisieren</span>
            </div>
          </div>

          <div className="text-center py-12 border-t border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Haben Sie Fragen zu Ihrem Deckenaufbau?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Jeder Altbau reagiert anders. Wir prüfen Ihre Bausubstanz vor Ort und erstellen ein fachgerechtes Sanierungskonzept für Schallschutz und Dämmung.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/kontakt"
                className="bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Beratungstermin vereinbaren
              </Link>
              <Link
                to="/blog/schallschutz-holzbalkendecke"
                className="bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Mehr zum Schallschutz
              </Link>
            </div>
          </div>
        </motion.article>
      </div>

      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default VaporBarrier;